import type { AxiosInstance } from 'axios';

export type LifecycleStage = 'NEW' | 'ACTIVE' | 'REPEAT' | 'AT_RISK' | 'DORMANT' | 'CHURNED';
export type VendorHealthGrade = 'A' | 'B' | 'C' | 'D' | 'F';
export type FraudEntityType =
  | 'USER'
  | 'VENDOR'
  | 'ORDER'
  | 'PAYMENT'
  | 'REFUND'
  | 'WALLET'
  | 'WITHDRAWAL'
  | 'REFERRAL'
  | 'COUPON';
export type FraudSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type FraudSignalStatus =
  | 'OPEN'
  | 'ACKNOWLEDGED'
  | 'ACTION_TAKEN'
  | 'DISMISSED'
  | 'FALSE_POSITIVE';
export type PricingSignalKind =
  | 'STOCK_PRESSURE'
  | 'DEMAND_SPIKE'
  | 'COMPETITION'
  | 'REGIONAL'
  | 'CLEARANCE'
  | 'FLASH';
export type PricingSignalStatus = 'PROPOSED' | 'APPLIED' | 'REJECTED' | 'EXPIRED';
export type ProductInteractionKind =
  | 'IMPRESSION'
  | 'CLICK'
  | 'VIEW'
  | 'ADD_TO_CART'
  | 'REMOVE_FROM_CART'
  | 'WISHLIST'
  | 'PURCHASE'
  | 'REVIEW';

export interface Customer360 {
  userId: string;
  totalOrders: number;
  totalSpendPaise: number;
  avgOrderValuePaise: number;
  repeatRateBps: number;
  churnRiskBps: number;
  lifecycleStage: LifecycleStage;
  firstOrderAt: string | null;
  lastOrderAt: string | null;
  lastSeenAt: string | null;
  preferredCategoryIds: string[];
  segments: string[];
  computedAt: string;
}

export interface Vendor360 {
  vendorId: string;
  gmvLifetimePaise: number;
  gmv30dPaise: number;
  totalOrders: number;
  deliveredOrders: number;
  returnedOrders: number;
  returnRateBps: number;
  fulfillmentRateBps: number;
  avgFulfillmentDays: number;
  slaBps: number;
  healthGrade: VendorHealthGrade;
  lastOrderAt: string | null;
  lastSettledAt: string | null;
  computedAt: string;
}

export interface Product360 {
  productId: string;
  vendorId: string;
  categoryId: string;
  impressions: number;
  clicks: number;
  addToCarts: number;
  orders: number;
  conversionBps: number;
  ctrBps: number;
  avgRatingX100: number;
  ratingCount: number;
  returnRateBps: number;
  revenueLifetimePaise: number;
  daysToFirstSale: number | null;
  stockOnHand: number;
  lastSoldAt: string | null;
  computedAt: string;
}

export interface FraudSignal {
  id: string;
  entityType: FraudEntityType;
  entityId: string;
  kind: string;
  severity: FraudSeverity;
  score: number;
  status: FraudSignalStatus;
  context: unknown | null;
  resolvedAt: string | null;
  note: string | null;
  createdAt: string;
}

export interface PricingSignal {
  id: string;
  variantId: string;
  kind: PricingSignalKind;
  suggestedDiscountBps: number;
  confidenceBps: number;
  status: PricingSignalStatus;
  validUntil: string | null;
  context: unknown | null;
  appliedAt: string | null;
  rejectedAt: string | null;
  createdAt: string;
}

export interface EtlRunResult {
  customersRefreshed: number;
  vendorsRefreshed: number;
  productsRefreshed: number;
  durationMs: number;
}

export function intelligenceEndpoints(http: AxiosInstance) {
  return {
    myCustomer: () => http.get<Customer360>('/intelligence/customer/me').then((r) => r.data),
    myVendor: () => http.get<Vendor360>('/intelligence/vendor/me').then((r) => r.data),
    recordInteraction: (body: {
      productId: string;
      variantId?: string;
      kind: ProductInteractionKind;
      anonymousId?: string;
      source?: string;
      metadata?: Record<string, unknown>;
    }) => http.post('/intelligence/interactions', body).then((r) => r.data),

    // Admin 360
    getCustomer: (userId: string) =>
      http.get<Customer360>(`/admin/intelligence/customers/${userId}`).then((r) => r.data),
    refreshCustomer: (userId: string) =>
      http.post<Customer360>(`/admin/intelligence/customers/${userId}/refresh`).then((r) => r.data),
    getVendor: (vendorId: string) =>
      http.get<Vendor360>(`/admin/intelligence/vendors/${vendorId}`).then((r) => r.data),
    refreshVendor: (vendorId: string) =>
      http.post<Vendor360>(`/admin/intelligence/vendors/${vendorId}/refresh`).then((r) => r.data),
    getProduct: (productId: string) =>
      http.get<Product360>(`/admin/intelligence/products/${productId}`).then((r) => r.data),
    refreshProduct: (productId: string) =>
      http
        .post<Product360>(`/admin/intelligence/products/${productId}/refresh`)
        .then((r) => r.data),

    // ETL
    runEtl: (body: { limit?: number; scopes?: ('customer' | 'vendor' | 'product')[] }) =>
      http.post<EtlRunResult>('/admin/intelligence/etl/run', body).then((r) => r.data),

    // Fraud
    listFraud: () =>
      http.get<{ data: FraudSignal[] }>('/admin/intelligence/fraud').then((r) => r.data.data),
    compositeRisk: () => http.get('/admin/intelligence/fraud/composite').then((r) => r.data),
    flagFraud: (body: {
      entityType: FraudEntityType;
      entityId: string;
      kind: string;
      score: number;
      severity?: FraudSeverity;
      note?: string;
    }) => http.post<FraudSignal>('/admin/intelligence/fraud/flag', body).then((r) => r.data),
    resolveFraud: (
      signalId: string,
      body: {
        status: 'ACKNOWLEDGED' | 'ACTION_TAKEN' | 'DISMISSED' | 'FALSE_POSITIVE';
        note?: string;
      },
    ) =>
      http
        .post<FraudSignal>(`/admin/intelligence/fraud/${signalId}/resolve`, body)
        .then((r) => r.data),

    // Pricing
    listPricing: () =>
      http.get<{ data: PricingSignal[] }>('/admin/intelligence/pricing').then((r) => r.data.data),
    proposePricing: (body: {
      variantId: string;
      kind: PricingSignalKind;
      suggestedDiscountBps: number;
      confidenceBps?: number;
      validUntil?: string;
    }) => http.post<PricingSignal>('/admin/intelligence/pricing/propose', body).then((r) => r.data),
    resolvePricing: (signalId: string, decision: 'APPLY' | 'REJECT') =>
      http
        .post<PricingSignal>(`/admin/intelligence/pricing/${signalId}/resolve`, { decision })
        .then((r) => r.data),
  };
}

export type IntelligenceEndpoints = ReturnType<typeof intelligenceEndpoints>;
